import { useEffect, useState } from "react"
import { useRouter, useSearchParamsCompat } from "@/lib/router-helpers"
import { Lock, ArrowLeft } from "lucide-react"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp"
import { useVerifyOTP, useSendOTP } from "@/hooks/use-auth"
import { getPendingPhone, clearPendingPhone } from "@/lib/auth"
import { useAuth } from "@/components/auth-provider"
import { toast } from "sonner"
import { Toaster } from "@/components/ui/sonner"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import Cookies from "js-cookie"

export default function OtpVerifyPage() {
  const router = useRouter()
  const [searchParams] = useSearchParamsCompat()
  const returnUrl = searchParams?.get("returnUrl")
  const { login } = useAuth()
  const [otp, setOtp] = useState("")
  const [mobile, setMobile] = useState<string | null>(null)
  const verifyOTPMutation = useVerifyOTP()
  const sendOTPMutation = useSendOTP()
  const { t } = useTranslation()
  const [isResending, setIsResending] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(60)

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);


  useEffect(() => {
    const p = getPendingPhone()
    setMobile(p)
    if (!p) router.push(`/auth/verify`)
  }, [router])

  async function resendOtp() {
    if (isResending || resendCooldown > 0 || !mobile) return;
    
    setIsResending(true);
    try {
        await sendOTPMutation.mutateAsync({ mobile })
        toast.success(t("OTP resent successfully"))
        setResendCooldown(60);
    } catch (error: any) {
        toast.error(error.message || t("Failed to resend OTP"))
    } finally {
        setIsResending(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault()
    if (otp.length !== 5 || !mobile) {
      toast.error(t("Please enter a valid 5-digit OTP"))
      return
    }

    try {
      const result = await verifyOTPMutation.mutateAsync({ mobile, otp })

      // If user and token are returned, user is registered
      if (result.message && result.status === "success") {
        // Step 1: perform login (stores token in cookie + localStorage)
        login(result.message)

        // Step 2: verify token is persisted correctly before proceeding
        if (typeof window !== "undefined") {
          const cookieToken = Cookies.get("qetero.token")
          const lsToken = window.localStorage.getItem("qetero.token")

          if (!cookieToken) {
            toast.error("Could not read authentication token from cookies. Please enable cookies and try again.")
          }

          if (!lsToken) {
            toast.error("Could not read authentication token from local storage. Please try again.")
          }

          // Require token to be available in BOTH places before moving to next step
          if (!cookieToken && !lsToken) {
            toast.error("Could not get authentication token in both cookie and local storage. Login cannot continue.")
            return
          }

          if (!cookieToken || !lsToken) {
            // Token is only partially stored; do not allow flow to continue
            toast.error("Authentication token was not stored correctly. Login cannot continue.")
            return
          }
        }

        clearPendingPhone()
        toast.success(t("Login successful"))
        const target = returnUrl ? decodeURIComponent(returnUrl) : `/`
        window.location.href = target
      }
    } catch (error: any) {
      // If 401, user is not registered - redirect to signup
      if (error.status === 401 || error.message?.includes("not registered")) {
        Cookies.set("qetero.verifiedPhone", mobile, { expires: 1/1440 * 2, path: '/' }) // 2 minutes

        toast.info(t("Account not found. Please create an account."))
        const registerUrl = returnUrl ? `/auth/register?returnUrl=${encodeURIComponent(returnUrl)}` : `/auth/register`
        router.push(registerUrl)
      } else {
        toast.error(error.message || t("Invalid OTP. Please try again."))
      }
    }
  }

  return (
    <>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
        }
      `}</style>

      <div className="min-h-[80vh] flex items-center justify-center px-4 pb-8 pt-4 md:pt-12">
        <div className="w-full max-w-lg animate-fade-in-up">
          <div className="rounded-3xl bg-white shadow-xl shadow-pink-50/50 border border-gray-100 p-6 md:p-10 relative overflow-hidden">
            <button 
              onClick={() => router.push('/auth/verify')}
              className="absolute top-4 left-4 p-2 rounded-full bg-white/80 hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-all z-20"
            >
              <ArrowLeft size={20} />
            </button>
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-pink-500 via-rose-500 to-pink-600" />
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-blue-50 rounded-full blur-3xl opacity-60 pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-blue-50 rounded-full blur-3xl opacity-60 pointer-events-none" />

            <div className="relative z-10 flex flex-col items-center text-center">
              <div className="w-20 h-20 rounded-full bg-pink-50 flex items-center justify-center shadow-sm mb-6 group transition-transform duration-500 hover:scale-110 hover:rotate-3">
                <Lock className="w-9 h-9 text-pink-600 group-hover:text-pink-500 transition-colors" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 tracking-tight">{t("VerifyYourNumber")}</h3>
              <p className="text-sm text-gray-500 mt-2">
                {t("Verification code sent to a phone number")}
              </p>
              <p className="text-lg font-bold text-pink-600 mt-1 tracking-wide">
                {mobile ?? ""}
              </p>
            </div>

            <form onSubmit={handleVerify} className="mt-8 relative z-10">
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-4 text-center">
                {t("OTP")}
              </label>

              <div className="flex justify-center">
                <InputOTP
                  maxLength={5}
                  value={otp}
                  autoFocus
                  onChange={setOtp}
                  disabled={verifyOTPMutation.isPending}
                  containerClassName="flex gap-2 sm:gap-3"
                >
                  {[0, 1, 2, 3, 4].map((index) => (
                    <InputOTPGroup key={index}>
                      <InputOTPSlot
                        index={index}
                        className={cn(
                          "w-10 h-12 sm:w-14 sm:h-16 rounded-xl border-2 text-lg sm:text-xl font-bold transition-all duration-200 bg-gray-50",
                          "focus:border-pink-500 focus:ring-4 focus:ring-pink-500/10 focus:bg-white focus:scale-105",
                          "data-[active=true]:border-pink-500 data-[active=true]:bg-white data-[active=true]:scale-105",
                          otp[index] ? "border-pink-500 bg-pink-50/30 text-pink-600" : "border-gray-100 text-gray-900"
                        )} />
                    </InputOTPGroup>
                  ))}
                </InputOTP>
              </div>

              <p className="text-xs text-gray-400 mt-4 text-center">
                {t("Enter the verification code sent.")}
              </p>

              <div className="flex flex-col items-center gap-4 mt-8">
                <button
                  type="submit"
                  disabled={verifyOTPMutation.isPending || otp.length !== 5}
                  className="w-full h-12 rounded-full bg-gradient-to-r from-pink-600 to-rose-600 text-white text-sm font-bold tracking-wide shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {verifyOTPMutation.isPending ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t("verifying")}
                    </span>
                  ) : (
                    t("verify")
                  )}
                </button>

                <button
                    type="button"
                    onClick={resendOtp}
                    disabled={isResending || resendCooldown > 0}
                    className={cn(
                        "text-xs font-semibold transition-colors py-2 px-4 rounded-full hover:bg-gray-50",
                        isResending || resendCooldown > 0
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-pink-600 hover:text-pink-700"
                    )}
                >
                    {isResending ? (
                        <span className="flex items-center gap-2">
                            <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            {t("Resending...")}
                        </span>
                    ) : resendCooldown > 0 ? (
                        <span>{t("Resend in")} {resendCooldown}s</span>
                    ) : (
                        t("Resend Code")
                    )}
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center mt-6 px-4 leading-relaxed">
                {t("By continuing, you agree to our")}{" "}
                <span className="text-pink-600 font-bold hover:underline cursor-pointer">{t("Terms of Service")}</span>{" "}
                {t("and")}{" "}
                <span className="text-pink-600 font-bold hover:underline cursor-pointer">{t("Privacy Policy")}</span>
              </p>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}
